import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Clock, MapPin, Zap, CreditCard, Smartphone, Banknote, ChevronDown, ChevronUp, Battery, Shield, Wrench, X, Car, Send, Check, AlertCircle, BookOpen, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AvisoLegal, PoliticaPrivacidad, PoliticaCookies, TerminosCondiciones } from './LegalPages';
import { mockData } from '../mock';
import useSEO from '../hooks/useSEO';
import { usePageTracking, useAnalytics } from '../hooks/useAnalytics';
import WhatsAppBookingFlow from './WhatsAppBookingFlow';
import EligibilityChecker from './EligibilityChecker';

const ArranqueExpressLanding = () => {
  // SEO: Set canonical and hreflang to avoid duplicate content issues
  useSEO({
    title: 'Arranque Coche Barcelona 24/7 - Desde 69€ | ArrancaYa BCN',
    description: 'ArrancaYa BCN - Servicio de arranque de coches con batería descargada en Barcelona 24/7. Llegamos en 30min, solo pagas si arranca. ¡Llama al 613 076 099!',
    canonical: 'https://arrancaya-bcn.es/',
    hreflang: {
      'es': 'https://arrancaya-bcn.es/',
      'ca': 'https://arrancaya-bcn.es/cat',
      'en': 'https://arrancaya-bcn.es/en',
      'x-default': 'https://arrancaya-bcn.es/'
    }
  });

  // Analytics: Track page views and events to MongoDB
  usePageTracking('/');
  const { trackWhatsAppClick, trackPhoneClick, trackFormOpen } = useAnalytics();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(() => {
    // Check if cookies were already accepted
    if (typeof window !== 'undefined') {
      return !localStorage.getItem('cookiesAccepted');
    }
    return true;
  });
  const [isWhatsAppFormOpen, setIsWhatsAppFormOpen] = useState(false);
  const [isEligibilityOpen, setIsEligibilityOpen] = useState(false);
  const [currentLegalPage, setCurrentLegalPage] = useState(null);
  const [formData, setFormData] = useState({
    marca: '',
    modelo: '',
    ubicacion: '',
    hora: 'Ahora',
    comentario: '',
    legalConsent: false
  });
  const [requestType, setRequestType] = useState(''); // 'arranque' o 'informacion'

  // Track page load
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'page_view',
      'page_title': 'Arranque Coche Barcelona',
      'page_location': window.location.href,
      'event_category': 'engagement',
      'event_label': 'landing_page_view'
    });
    console.log('Page view tracked');
  }, []);

  const handleWhatsAppClick = () => {
    // Track to MongoDB analytics
    trackWhatsAppClick('/', { source: 'hero_button' });
    trackFormOpen('/', 'whatsapp_booking');
    
    // Tracking GTM/GA4 - WhatsApp form open
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'whatsapp_form_open',
      'event_category': 'engagement',
      'event_label': 'whatsapp_form',
      'value': 1
    });
    console.log('WhatsApp form open tracked');
    setIsWhatsAppFormOpen(true);
  };

  const handleFormSubmit = () => {
    if (!formData.legalConsent) {
      alert('Debes aceptar los términos y condiciones');
      return;
    }

    const message = `Hola, necesito servicio de arranque de coche:
Marca: ${formData.marca}
Modelo: ${formData.modelo}
Ubicación: ${formData.ubicacion}
Hora deseada: ${formData.hora}
Comentarios: ${formData.comentario || 'Ninguno'}`;
    
    // Tracking GTM/GA4 - WhatsApp message sent (conversion)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'whatsapp_conversion',
      'event_category': 'conversion',
      'event_label': 'whatsapp_message_sent',
      'value': 1,
      'car_brand': formData.marca,
      'location': formData.ubicacion,
      'request_type': 'arranque'
    });
    console.log('WhatsApp conversion tracked', formData);
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${mockData.contacts.whatsapp.replace(/\s/g, '')}?text=${encodedMessage}`, '_blank');
    handleCloseModal();
  };
  const handleCloseModal = () => {
    setIsWhatsAppFormOpen(false);
    setRequestType('');
    
    // Reset form
    setFormData({
      marca: '',
      modelo: '',
      ubicacion: '',
      hora: 'Ahora',
      comentario: '',
      legalConsent: false
    });
  };

  const handlePhoneClick = () => {
    // Track to MongoDB analytics
    trackPhoneClick('/', { source: 'hero_button' });
    
    // Tracking GTM/GA4 - Phone call
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'phone_call',
      'event_category': 'engagement',
      'event_label': 'phone_click',
      'value': 1
    });
    console.log('Phone click tracked');
    window.location.href = `tel:${mockData.contacts.whatsapp}`;
  };

  const acceptCookies = () => {
    setShowCookieBanner(false);
    // Store cookie preference
    localStorage.setItem('cookiesAccepted', 'true');
    
    // Tracking GTM/GA4 - Cookie acceptance
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'cookie_consent',
      'event_category': 'engagement',
      'event_label': 'cookies_accepted',
      'value': 1
    });
  };

  const trackLegalPageView = (pageName) => {
    // Tracking GTM/GA4 - Legal page views
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'legal_page_view',
      'event_category': 'engagement',
      'event_label': pageName,
      'value': 1
    });
    console.log(`Legal page view tracked: ${pageName}`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src={mockData.company.logo} 
                alt="ArrancaYa BCN" 
                className="h-10 md:h-12 w-auto"
              />
            </div>
            
            {/* Navigation Desktop */}
            <nav className="hidden md:flex space-x-8 items-center">
              <a href="#servicios" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Servicios</a>
              <a href="#precios" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Precios</a>
              <a href="#como-funciona" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Cómo funciona</a>
              <Link to="/blog" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Blog</Link>
              
              {/* Language Selector */}
              <div className="border-l border-gray-200 pl-6">
                <select 
                  onChange={(e) => window.location.href = e.target.value}
                  value="/"
                  className="appearance-none bg-gray-100 border-0 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="/">🇪🇸 ES</option>
                  <option value="/cat">🔴 CA</option>
                  <option value="/en">🇬🇧 EN</option>
                </select>
              </div>
              
              {/* CTA Button */}
              <Button 
                onClick={() => setIsWhatsAppFormOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-6 py-2 font-bold"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Contactar
              </Button>
            </nav>
            
            {/* Mobile CTA */}
            <div className="md:hidden">
              <Button 
                onClick={() => setIsWhatsAppFormOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 text-sm font-bold"
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                Contactar
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-white py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left">
                <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Zap className="h-4 w-4 mr-2" />
                  Servicio 24/7 en Barcelona
                </div>
                
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-6 tracking-tight">
                  ¿Tu coche no arranca?
                  <span className="block text-red-600">Te lo arrancamos hoy mismo</span>
                </h1>
                
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Servicio profesional de arranque a domicilio con batería portátil. 
                  <strong className="text-gray-900"> Sin grúa, sin esperas, precio fijo.</strong>
                </p>
                
                {/* Trust Points */}
                <div className="space-y-3 mb-8">
                  {[
                    { text: "Servicio express en Barcelona", icon: Clock },
                    { text: "Precio fijo: 69€ día / 89€ noche / 99€ finde", icon: CreditCard },
                    { text: "Solo pagas si tu coche arranca", icon: Shield }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-gray-700 font-medium">{item.text}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <Button 
                    onClick={handleWhatsAppClick}
                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-xl flex items-center justify-center space-x-3 transition-all duration-200 shadow-lg hover:shadow-xl rounded-xl font-bold"
                    data-testid="hero-whatsapp-btn"
                  >
                    <MessageCircle className="h-6 w-6" />
                    <span>Solicitar Arranque</span>
                  </Button>
                  
                  <Button 
                    onClick={handlePhoneClick}
                    variant="outline"
                    className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-6 text-xl flex items-center justify-center space-x-3 transition-all duration-200 rounded-xl font-bold"
                    data-testid="hero-phone-btn"
                  >
                    <Phone className="h-6 w-6" />
                    <span>613 076 099</span>
                  </Button>
                </div>
                
                {/* Third CTA - Eligibility Check */}
                <div className="mb-6">
                  <Button 
                    onClick={() => setIsEligibilityOpen(true)}
                    variant="ghost"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 font-medium flex items-center space-x-2"
                    data-testid="hero-eligibility-btn"
                  >
                    <Car className="h-5 w-5" />
                    <span>¿Mi coche es compatible?</span>
                  </Button>
                </div>
                
                {/* Languages */}
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <span>Atendemos en:</span>
                  <span>🇪🇸 Español</span>
                  <span>•</span>
                  <span>🇬🇧 English</span>
                  <span>•</span>
                  <span>🇫🇷 Français</span>
                </div>
              </div>
              
              {/* Right - Image + Trust Badge */}
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={mockData.company.heroImage}
                    alt="Técnico ArrancaYa BCN realizando arranque profesional"
                    className="w-full h-auto object-cover"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
                
                {/* Floating Badge */}
                <div className="absolute -bottom-4 left-4 bg-white rounded-xl shadow-xl p-4 border border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Zap className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Más rápido</p>
                      <p className="text-sm text-gray-500">que tu seguro</p>
                    </div>
                  </div>
                </div>
                
                {/* Second Floating Badge */}
                <div className="absolute -top-4 -right-4 bg-green-600 text-white rounded-xl shadow-xl px-4 py-3">
                  <p className="font-bold text-lg">Desde 69€</p>
                  <p className="text-sm text-green-100">IVA incluido</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Car Brands Scrolling Banner */}
      <section className="py-8 bg-white border-y border-gray-200 overflow-hidden">
        <div className="relative">
          <div className="flex animate-scroll space-x-8 md:space-x-12">
            {[...mockData.carBrands, ...mockData.carBrands, ...mockData.carBrands].map((brand, index) => (
              <div key={index} className="flex items-center space-x-2 text-gray-500 whitespace-nowrap flex-shrink-0">
                <Car className="h-4 w-4 md:h-5 md:w-5" />
                <span className="text-base md:text-lg font-medium">{brand}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block bg-red-100 text-red-700 px-4 py-1 rounded-full text-sm font-medium mb-4">
              Nuestros Servicios
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Especialistas en Arranque de Coches
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Servicio profesional para vehículos 12V en toda Barcelona.
              <span className="text-green-600 font-medium"> Compatible con coches de alquiler.</span>
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
            {[
              { icon: Battery, title: "Arranque 12V", desc: "Batería portátil profesional para todo tipo de vehículos", color: "red" },
              { icon: Clock, title: "24/7 Todo el Año", desc: "Disponibles día, noche, festivos y fines de semana", color: "blue" },
              { icon: Shield, title: "Garantía Total", desc: "Si no arranca, no pagas. Desplazamiento gratuito", color: "green" }
            ].map((service, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
                <div className={`w-16 h-16 bg-${service.color}-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <service.icon className={`h-8 w-8 text-${service.color}-600`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>

          {/* Key Points */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">¿Por qué elegirnos?</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {mockData.company.keyPoints.map((point, index) => (
                  <div key={index} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-xl">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <p className="text-gray-700">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">¿Por qué elegir ArrancaYa BCN?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comparamos nuestro servicio con otros dépanneurs tradicionales
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-red-600">
                    <tr>
                      <th className="px-6 py-4 text-left text-white font-semibold">Características</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">
                        <div className="flex items-center justify-center space-x-2">
                          <img 
                            src={mockData.company.logo} 
                            alt="ArrancaYa BCN" 
                            className="h-6 w-auto"
                          />
                          <span>ArrancaYa</span>
                        </div>
                      </th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Otros Servicios</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-gray-900 font-medium">
                        Arranque sin cambiar la batería
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <Check className="h-6 w-6 text-green-600" />
                          <span className="ml-2 text-green-600 font-semibold">SÍ</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <X className="h-6 w-6 text-red-600" />
                          <span className="ml-2 text-red-600 font-semibold">NO</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 bg-blue-25">
                      <td className="px-6 py-4 text-gray-900 font-medium">
                        Precio fijo y transparente
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <Check className="h-6 w-6 text-green-600" />
                          <span className="ml-2 text-green-600 font-semibold">SÍ</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <X className="h-6 w-6 text-red-600" />
                          <span className="ml-2 text-red-600 font-semibold">NO</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-gray-900 font-medium">
                        Llegamos en menos de 1 hora
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <Check className="h-6 w-6 text-green-600" />
                          <span className="ml-2 text-green-600 font-semibold">SÍ</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <X className="h-6 w-6 text-red-600" />
                          <span className="ml-2 text-red-600 font-semibold">NO</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 bg-blue-25">
                      <td className="px-6 py-4 text-gray-900 font-medium">
                        Pagas solo si arrancas
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <Check className="h-6 w-6 text-green-600" />
                          <span className="ml-2 text-green-600 font-semibold">SÍ</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <X className="h-6 w-6 text-red-600" />
                          <span className="ml-2 text-red-600 font-semibold">NO</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <p className="text-lg text-gray-700 mb-4">
                <strong>La diferencia está clara:</strong> ArrancaYa BCN te ofrece un servicio sin riesgos
              </p>
              <Button 
                onClick={() => setIsWhatsAppFormOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg"
              >
                Elegir ArrancaYa Ahora
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precios" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block bg-green-100 text-green-700 px-4 py-1 rounded-full text-sm font-medium mb-4">
              Precios Transparentes
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tarifas Fijas, Sin Sorpresas
            </h2>
            <p className="text-gray-600 text-lg">Desplazamiento incluido. Solo pagas si arranca.</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Day Rate */}
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 relative hover:border-green-500 transition-colors">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Tarifa Día
                  </span>
                </div>
                <div className="text-center pt-4">
                  <p className="text-6xl font-black text-gray-900 mb-2">
                    {mockData.pricing.day.price}<span className="text-2xl">€</span>
                  </p>
                  <p className="text-gray-600 mb-6">IVA incluido</p>
                  
                  <div className="space-y-3 text-left mb-8">
                    {["Lunes a Viernes", "8:00 - 22:00", "Servicio express"].map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <Check className="h-5 w-5 text-green-500" />
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    onClick={() => setIsWhatsAppFormOpen(true)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold"
                  >
                    Solicitar Ahora
                  </Button>
                </div>
              </div>
              
              {/* Night/Weekend Rate */}
              <div className="bg-gray-900 rounded-2xl p-8 relative text-white">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Noche + Fines de Semana
                  </span>
                </div>
                <div className="text-center pt-4">
                  <p className="text-6xl font-black text-white mb-2">
                    {mockData.pricing.nightWeekend.price}<span className="text-2xl">€</span>
                  </p>
                  <p className="text-gray-400 mb-6">IVA incluido</p>
                  
                  <div className="space-y-3 text-left mb-8">
                    {["Noches 22:00 - 8:00", "Sábados y Domingos", "Festivos incluidos"].map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <Check className="h-5 w-5 text-green-400" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    onClick={handlePhoneClick}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-xl font-bold"
                  >
                    <Phone className="h-5 w-5 mr-2" />
                    Llamar: 613 076 099
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Payment Methods */}
            <div className="mt-12 text-center">
              <p className="text-gray-600 mb-4">Aceptamos múltiples formas de pago:</p>
              <div className="flex justify-center flex-wrap gap-6">
                {[
                  { icon: Smartphone, name: "Bizum" },
                  { icon: CreditCard, name: "Tarjeta" },
                  { icon: Banknote, name: "Efectivo" }
                ].map((method, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-lg">
                    <method.icon className="h-5 w-5 text-gray-600" />
                    <span className="text-gray-700 font-medium">{method.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="como-funciona" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Cómo Funciona</h2>
            <p className="text-gray-600">Proceso simple y rápido en 3 pasos</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {mockData.howItWorks.map((step, index) => (
              <div key={index} className="flex items-start mb-8 last:mb-0">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-6">
                  {step.step}
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button 
              onClick={() => setIsWhatsAppFormOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg"
            >
              Solicitar Servicio Ahora
            </Button>
          </div>
        </div>
      </section>

      {/* Zones Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Zona de Servicio</h2>
            <p className="text-gray-600">Cobertura completa en Barcelona y alrededores</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Barcelona Ciudad */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-center mb-2">Barcelona Ciudad</h3>
              <p className="text-gray-600 text-sm text-center mb-3">
                Todos los barrios de Barcelona
              </p>
              <p className="text-green-600 font-bold text-center">Tarifa estándar</p>
            </div>
            
            {/* Área Metropolitana */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-center mb-2">Área Metropolitana</h3>
              <p className="text-gray-600 text-sm text-center mb-3">
                L'Hospitalet, Badalona, Santa Coloma...
              </p>
              <p className="text-blue-600 font-bold text-center">Consultar disponibilidad</p>
            </div>
            
            {/* Aeropuerto El Prat */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl shadow-sm border border-yellow-200">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">✈️</span>
              </div>
              <h3 className="text-lg font-bold text-center mb-2">Aeropuerto El Prat</h3>
              <p className="text-gray-600 text-sm text-center mb-3">
                Terminales T1 y T2, parkings
              </p>
              <p className="text-yellow-700 font-bold text-center">
                +25€ suplemento
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Preguntas Frecuentes</h2>
            <p className="text-gray-600">Resolvemos tus dudas más comunes</p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {mockData.faq.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-gray-50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-medium hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Blog Section - NEW */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              📖 Guías y Consejos sobre Batería del Coche
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Aprende todo sobre <strong>batería descargada</strong>, arranque con pinzas y mantenimiento preventivo
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Artículo destacado 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="bg-green-500 p-3 rounded-full">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <span className="text-sm text-blue-600 font-medium">TUTORIAL</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Cómo Arrancar el Coche con Pinzas
              </h3>
              <p className="text-gray-700 mb-4">
                Tutorial paso a paso para arrancar tu coche con cables de arranque de forma segura y profesional.
              </p>
              <Link 
                to="/blog/arrancar-coche-con-pinzas"
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    'event': 'blog_access',
                    'event_category': 'engagement',
                    'event_label': 'home_pinzas_article',
                    'value': 1
                  });
                }}
              >
                Leer guía completa →
              </Link>
            </div>

            {/* Artículo destacado 2 */}
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="bg-orange-500 p-3 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <span className="text-sm text-orange-600 font-medium">CONSEJOS</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                ¿Se Puede Arrancar Empujando?
              </h3>
              <p className="text-gray-700 mb-4">
                Descubre cuándo es posible, los riesgos y por qué es mejor llamar a un profesional.
              </p>
              <Link 
                to="/blog/arrancar-empujando-coche"
                className="inline-flex items-center text-orange-600 font-semibold hover:text-orange-800"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    'event': 'blog_access',
                    'event_category': 'engagement',
                    'event_label': 'home_empuje_article',
                    'value': 1
                  });
                }}
              >
                Leer artículo →
              </Link>
            </div>

            {/* Artículo destacado 3 */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="bg-blue-500 p-3 rounded-full">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <span className="text-sm text-blue-600 font-medium">PREVENCIÓN</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Cómo Prevenir Batería Descargada
              </h3>
              <p className="text-gray-700 mb-4">
                Tips profesionales para evitar quedarte sin batería y ahorrar en servicios de emergencia.
              </p>
              <Link 
                to="/blog/prevenir-bateria-descargada"
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    'event': 'blog_access',
                    'event_category': 'engagement',
                    'event_label': 'home_prevencion_article',
                    'value': 1
                  });
                }}
              >
                Leer consejos →
              </Link>
            </div>
          </div>

          {/* CTA vers le blog complet */}
          <div className="text-center">
            <Link 
              to="/blog"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  'event': 'blog_access',
                  'event_category': 'engagement',
                  'event_label': 'home_blog_cta',
                  'value': 1
                });
              }}
            >
              <BookOpen className="h-5 w-5 mr-2" />
              Ver Todos los Artículos del Blog
            </Link>
            <p className="text-gray-600 mt-3">
              Más de 6 artículos sobre <strong>batería del coche</strong>, arranque y mantenimiento
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-16 bg-red-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={mockData.barcelonaImages[1]} 
            alt="Barcelona skyline"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl font-bold mb-4">¿Necesitas arrancar tu coche?</h2>
          <p className="text-xl mb-8 text-red-100">
            Contacta ahora - Servicio rápido y profesional en Barcelona
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <Button 
              onClick={() => setIsWhatsAppFormOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg flex items-center justify-center space-x-2"
            >
              <MessageCircle className="h-5 w-5" />
              <span>WhatsApp</span>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Section Logo et informations principales */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start mb-4">
                <span className="text-xl font-bold">ArrancaYa BCN</span>
              </div>
              <p className="text-gray-400 mb-4">
                Plataforma que te conecta con técnicos independientes homologados para el arranque de tu vehículo en Barcelona
              </p>
            </div>
            
            {/* Section Services rapides */}
            <div className="text-center md:text-left">
              <h3 className="text-lg font-semibold mb-4">Servicios</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Arranque 12V</li>
                <li>Servicio 24/7</li>
                <li>A domicilio</li>
                <li>Precio fijo</li>
              </ul>
            </div>
            
            {/* Section Contact */}
            <div className="text-center md:text-left">
              <h3 className="text-lg font-semibold mb-4">Contacto</h3>
              <div className="space-y-2 text-gray-400">
                <p>Barcelona Ciudad</p>
                <p>Servicio 24/7</p>
                <Button 
                  onClick={() => setIsWhatsAppFormOpen(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm"
                >
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>
          
          {/* Section mentions légales - préparée pour les textes futurs */}
          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-sm text-gray-500">
                <p>© 2026 ArrancaYa BCN. Todos los derechos reservados.</p>
              </div>
              <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-6 text-sm mb-20">
                <button 
                  onClick={() => {
                    trackLegalPageView('aviso_legal');
                    setCurrentLegalPage('aviso');
                  }}
                  className="text-gray-400 hover:text-white transition-colors text-left"
                >
                  Aviso Legal
                </button>
                <button 
                  onClick={() => {
                    trackLegalPageView('politica_privacidad');
                    setCurrentLegalPage('privacidad');
                  }}
                  className="text-gray-400 hover:text-white transition-colors text-left"
                >
                  Política de Privacidad
                </button>
                <button 
                  onClick={() => {
                    trackLegalPageView('politica_cookies');
                    setCurrentLegalPage('cookies');
                  }}
                  className="text-gray-400 hover:text-white transition-colors text-left"
                >
                  Política de Cookies
                </button>
                <button 
                  onClick={() => {
                    trackLegalPageView('terminos_condiciones');
                    setCurrentLegalPage('terminos');
                  }}
                  className="text-gray-400 hover:text-white transition-colors text-left"
                >
                  Términos y Condiciones
                </button>
                <Link 
                  to="/blog"
                  className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                  onClick={() => {
                    // Track blog access from footer
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      'event': 'blog_access',
                      'event_category': 'engagement', 
                      'event_label': 'footer_link',
                      'value': 1
                    });
                  }}
                >
                  📖 Blog Batería Coche
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky WhatsApp Button for Mobile - Z-index plus élevé */}
      <div className="fixed bottom-6 right-6 z-[60] md:hidden">
        <Button 
          onClick={() => setIsWhatsAppFormOpen(true)}
          className="bg-green-600 hover:bg-green-700 text-white rounded-full w-14 h-14 shadow-lg flex items-center justify-center"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      </div>

      {/* Cookie Banner */}
      {showCookieBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 shadow-lg z-[50]">
          <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 relative">
            <div className="flex-1">
              <p className="text-sm">
                Utilizamos cookies para mejorar tu experiencia. Al continuar navegando, aceptas nuestro uso de cookies.
              </p>
            </div>
            <div className="flex space-x-2">
              <Button 
                onClick={acceptCookies}
                className="bg-blue-400 hover:bg-blue-500 text-white px-4 py-2 text-sm"
              >
                Aceptar
              </Button>
              <Button 
                onClick={() => setShowCookieBanner(false)}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-800 px-3 py-2 text-sm"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* New WhatsApp Booking Flow - Futuristic Modal */}
      <WhatsAppBookingFlow 
        isOpen={isWhatsAppFormOpen} 
        onClose={handleCloseModal}
        phoneNumber={mockData.contacts.whatsapp}
      />

      {/* Eligibility Checker Modal */}
      <EligibilityChecker
        isOpen={isEligibilityOpen}
        onClose={() => setIsEligibilityOpen(false)}
        onRequestService={() => setIsWhatsAppFormOpen(true)}
        lang="es"
      />

      {/* Legal Pages Modals */}
      {currentLegalPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {currentLegalPage === 'aviso' && <AvisoLegal onClose={() => setCurrentLegalPage(null)} />}
            {currentLegalPage === 'privacidad' && <PoliticaPrivacidad onClose={() => setCurrentLegalPage(null)} />}
            {currentLegalPage === 'cookies' && <PoliticaCookies onClose={() => setCurrentLegalPage(null)} />}
            {currentLegalPage === 'terminos' && <TerminosCondiciones onClose={() => setCurrentLegalPage(null)} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArranqueExpressLanding;